"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { useReportWebVitals } from "next/web-vitals";

import {
  JOURNEY_EVENTS,
  routeTemplate,
  trackEvent,
  useAnalyticsTracker,
  useArrivalTracker,
  type JourneyEntity,
  type JourneyEventName,
} from "@/lib/analytics";
import { captureInboundShareRef } from "@/lib/share-links";

// Pages where reading is the product: a visit counts as qualified after 10 s
// and half the page scrolled.
const QUALIFIED_PREFIXES = [
  "/dashboard",
  "/markets",
  "/w",
  "/strategies",
  "/dashboard/market-signals",
];
const QUALIFIED_DWELL_MS = 10_000;
const QUALIFIED_DEPTH = 0.5;

function isQualifiedRoute(route: string) {
  return QUALIFIED_PREFIXES.some(
    (p) => route === p || route.startsWith(`${p}/`),
  );
}

// Server-rendered pages declare events in markup:
//   <a data-journey="markets.asset_opened" data-j-from="list" …>   on click
//   <span data-journey-view="markets.searched" data-j-result-band="6-20" hidden />
// data-j-* attributes become properties (data-j-asset-group → asset_group);
// data-journey-entity-type / -ref name what the event is about.
function markupEvent(el: HTMLElement, attr: "journey" | "journeyView") {
  const name = el.dataset[attr] as JourneyEventName | undefined;
  if (!name || !(name in JOURNEY_EVENTS)) return;
  const props: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(el.dataset)) {
    // data-j-from → dataset.jFrom; skips data-journey* themselves.
    if (!/^j[A-Z]/.test(k) || v === undefined) continue;
    const key =
      k[1]!.toLowerCase() +
      k.slice(2).replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
    props[key] =
      v === "true"
        ? true
        : v === "false"
          ? false
          : /^\d+$/.test(v)
            ? Number(v)
            : v;
  }
  const type = el.dataset.journeyEntityType as
    | JourneyEntity["type"]
    | undefined;
  const ref = el.dataset.journeyEntityRef;
  trackEvent(name, props as never, type && ref ? { type, ref } : undefined);
}

function vitalBand(name: string, value: number): string {
  if (name === "CLS")
    return value < 0.1 ? "good" : value < 0.25 ? "needs_improvement" : "poor";
  const [good, poor] =
    name === "LCP"
      ? [2500, 4000]
      : name === "INP"
        ? [200, 500]
        : name === "TTFB"
          ? [800, 1800]
          : [1800, 3000];
  return value < good ? "good" : value < poor ? "needs_improvement" : "poor";
}

export function AnalyticsTracker() {
  useAnalyticsTracker();
  useArrivalTracker();

  const pathname = usePathname();
  const page = useRef<{
    route: string;
    since: number;
    depth: number;
    qualified: boolean;
  } | null>(null);

  useReportWebVitals((metric) => {
    if (!["LCP", "INP", "CLS", "TTFB", "FCP"].includes(metric.name)) return;
    trackEvent("web_vitals.reported", {
      metric: metric.name,
      value_band: vitalBand(metric.name, metric.value),
      route: routeTemplate(window.location.pathname),
    });
  });

  // ?ref= / ?src= / ?join= on any page keep their attribution for the session;
  // ?fexr_push= (set by the service worker on a notification click) is a
  // push open.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    captureInboundShareRef(params);
    const push = params.get("fexr_push");
    if (push) {
      trackEvent("push.opened", { campaign: push.slice(0, 60) });
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete("fexr_push");
        window.history.replaceState(window.history.state, "", url.toString());
      } catch {}
    }
  }, [pathname]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const el = (e.target as Element | null)?.closest?.("[data-journey]");
      if (el instanceof HTMLElement) markupEvent(el, "journey");
    };
    let timer: ReturnType<typeof setTimeout> | null = null;
    const scan = () => {
      timer = null;
      document
        .querySelectorAll<HTMLElement>(
          "[data-journey-view]:not([data-journey-seen])",
        )
        .forEach((el) => {
          el.dataset.journeySeen = "1";
          markupEvent(el, "journeyView");
        });
    };
    const observer = new MutationObserver(() => {
      if (!timer) timer = setTimeout(scan, 300);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    scan();
    document.addEventListener("click", onClick, { capture: true });
    return () => {
      observer.disconnect();
      if (timer) clearTimeout(timer);
      document.removeEventListener("click", onClick, { capture: true });
    };
  }, []);

  // Page view, time on page, qualified visit.
  useEffect(() => {
    if (!pathname) return;
    const route = routeTemplate(pathname);
    const prev = page.current;
    if (prev && prev.route !== route) {
      trackEvent("screen.exited", {
        route: prev.route,
        dwell_ms: Date.now() - prev.since,
        exit_reason: "navigate",
      });
    }
    if (!prev || prev.route !== route) {
      page.current = { route, since: Date.now(), depth: 0, qualified: false };
      trackEvent("screen.viewed", { route });
    }
  }, [pathname]);

  useEffect(() => {
    const maybeQualify = () => {
      const p = page.current;
      if (!p || p.qualified || !isQualifiedRoute(p.route)) return;
      // A page that fits on screen counts as fully read.
      const doc = document.scrollingElement || document.documentElement;
      if (doc.scrollHeight <= doc.clientHeight + 40) p.depth = 1;
      const dwell = Date.now() - p.since;
      if (dwell >= QUALIFIED_DWELL_MS && p.depth >= QUALIFIED_DEPTH) {
        p.qualified = true;
        trackEvent("discovery.visit.qualified", {
          dwell_ms: dwell,
          max_scroll_depth: Math.round(p.depth * 100) / 100,
        });
      }
    };
    // Dashboard pages scroll inside a panel, so listen on capture and read
    // whichever element scrolled.
    let raf = 0;
    const onScroll = (e: Event) => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const p = page.current;
        if (!p) return;
        const el =
          e.target instanceof Element
            ? e.target
            : document.scrollingElement || document.documentElement;
        // The sidebar, menus and dialogs scroll too; they aren't the page.
        if (
          el.closest("nav, aside, [role=dialog], [role=menu], [role=listbox]")
        )
          return;
        const max = el.scrollHeight - el.clientHeight;
        const depth = max > 0 ? Math.min(1, el.scrollTop / max) : 1;
        if (depth > p.depth) p.depth = depth;
        maybeQualify();
      });
    };
    const tick = setInterval(maybeQualify, 5_000);
    const onVisibility = () => {
      const p = page.current;
      if (document.visibilityState === "hidden") {
        if (p)
          trackEvent("screen.exited", {
            route: p.route,
            dwell_ms: Date.now() - p.since,
            exit_reason: "hidden",
          });
        trackEvent("app.session.backgrounded");
      } else {
        if (p) p.since = Date.now();
        trackEvent("app.session.foregrounded");
      }
    };
    document.addEventListener("scroll", onScroll, {
      capture: true,
      passive: true,
    });
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("scroll", onScroll, { capture: true });
      document.removeEventListener("visibilitychange", onVisibility);
      clearInterval(tick);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return null;
}
